// 🔄 Unified Storage Service for Cross-Platform Session Persistence
// Handles localStorage, Capacitor Storage, and Electron storage seamlessly

class UnifiedStorage {
  constructor() {
    this.isCapacitor = typeof window !== 'undefined' && window.Capacitor;
    this.isElectron = typeof window !== 'undefined' && window.require && window.require('electron');
    this.storage = null;
    this.init();
  }

  async init() {
    try {
      if (this.isCapacitor) {
        // Initialize storage with Android Auth Persistence support
        this.storage = {
          get: async (key) => {
            try {
              // For auth-related keys, use the custom plugin
              if (key && (key.includes('auth') || key.includes('token') || key.includes('user'))) {
                const AndroidAuthPersistence = (await import('./androidAuthPersistence.js')).default;
                const result = await AndroidAuthPersistence.getAuthToken();
                return result.token;
              }
              // For other keys, try secure storage first
              try {
                const { SecureStorage } = await import('@aparajita/capacitor-secure-storage');
                const result = await SecureStorage.get({ key });
                return result.value;
              } catch (secureError) {
                // Fallback to regular storage
                const { Storage } = await import('@capacitor/storage');
                const result = await Storage.get({ key });
                return result.value;
              }
            } catch (error) {
              console.warn('Storage get error:', error);
              return null;
            }
          },
          set: async (key, value) => {
            try {
              // For auth-related keys, use the custom plugin
              if (key && (key.includes('auth') || key.includes('token') || key.includes('user'))) {
                const AndroidAuthPersistence = (await import('./androidAuthPersistence.js')).default;
                const expiry = (Date.now() + (24 * 60 * 60 * 1000)).toString(); // 24 hours
                await AndroidAuthPersistence.setAuthToken(value, expiry);
                return;
              }
              // For other keys, try secure storage first
              try {
                const { SecureStorage } = await import('@aparajita/capacitor-secure-storage');
                await SecureStorage.set({ key, value });
                return;
              } catch (secureError) {
                // Fallback to regular storage
                const { Storage } = await import('@capacitor/storage');
                await Storage.set({ key, value });
              }
            } catch (error) {
              console.warn('Storage set error:', error);
            }
          },
          remove: async (key) => {
            try {
              // For auth-related keys, use the custom plugin
              if (key && (key.includes('auth') || key.includes('token') || key.includes('user'))) {
                const AndroidAuthPersistence = (await import('./androidAuthPersistence.js')).default;
                await AndroidAuthPersistence.clearAuthToken();
                return;
              }
              // For other keys, try secure storage first
              try {
                const { SecureStorage } = await import('@aparajita/capacitor-secure-storage');
                await SecureStorage.remove({ key });
                return;
              } catch (secureError) {
                // Fallback to regular storage
                const { Storage } = await import('@capacitor/storage');
                await Storage.remove({ key });
              }
            } catch (error) {
              console.warn('Storage remove error:', error);
            }
          },
          clear: async () => {
            try {
              // Clear auth tokens
              const AndroidAuthPersistence = (await import('./androidAuthPersistence.js')).default;
              await AndroidAuthPersistence.clearAuthToken();
              
              // Clear other storage
              try {
                const { SecureStorage } = await import('@aparajita/capacitor-secure-storage');
                await SecureStorage.clear();
              } catch (secureError) {
                const { Storage } = await import('@capacitor/storage');
                await Storage.clear();
              }
            } catch (error) {
              console.warn('Storage clear error:', error);
            }
          }
        };
        console.log('✅ Unified Storage with Android Auth Persistence initialized');
        return;
      } else if (this.isElectron) {
        // Use Electron's secure storage
        this.storage = {
          get: (key) => this.getElectronStorage(key),
          set: (key, value) => this.setElectronStorage(key, value),
          remove: (key) => this.removeElectronStorage(key),
          clear: () => this.clearElectronStorage()
        };
      } else {
        // Use localStorage for web
        this.storage = {
          get: (key) => Promise.resolve(localStorage.getItem(key)),
          set: (key, value) => Promise.resolve(localStorage.setItem(key, value)),
          remove: (key) => Promise.resolve(localStorage.removeItem(key)),
          clear: () => Promise.resolve(localStorage.clear())
        };
      }
    } catch (error) {
      console.warn('Storage initialization failed, falling back to localStorage:', error);
      this.storage = {
        get: (key) => Promise.resolve(localStorage.getItem(key)),
        set: (key, value) => Promise.resolve(localStorage.setItem(key, value)),
        remove: (key) => Promise.resolve(localStorage.removeItem(key)),
        clear: () => Promise.resolve(localStorage.clear())
      };
    }
  }

  // Electron storage methods
  getElectronStorage(key) {
    try {
      const { ipcRenderer } = window.require('electron');
      return ipcRenderer.invoke('storage-get', key);
    } catch (error) {
      console.error('Electron storage get error:', error);
      return Promise.resolve(localStorage.getItem(key));
    }
  }

  setElectronStorage(key, value) {
    try {
      const { ipcRenderer } = window.require('electron');
      return ipcRenderer.invoke('storage-set', key, value);
    } catch (error) {
      console.error('Electron storage set error:', error);
      return Promise.resolve(localStorage.setItem(key, value));
    }
  }

  removeElectronStorage(key) {
    try {
      const { ipcRenderer } = window.require('electron');
      return ipcRenderer.invoke('storage-remove', key);
    } catch (error) {
      console.error('Electron storage remove error:', error);
      return Promise.resolve(localStorage.removeItem(key));
    }
  }

  clearElectronStorage() {
    try {
      const { ipcRenderer } = window.require('electron');
      return ipcRenderer.invoke('storage-clear');
    } catch (error) {
      console.error('Electron storage clear error:', error);
      return Promise.resolve(localStorage.clear());
    }
  }

  // Public API methods
  async getItem(key) {
    try {
      if (!this.storage) await this.init();
      
      if (this.isCapacitor && this.storage.get) {
        // Use Capacitor Storage API
        const result = await this.storage.get({ key });
        return result?.value || null;
      } else {
        // Use direct storage method
        const value = await this.storage.get(key);
        return value;
      }
    } catch (error) {
      console.error('Storage get error:', error);
      // Fallback to localStorage
      try {
        return localStorage.getItem(key);
      } catch (fallbackError) {
        console.error('LocalStorage fallback error:', fallbackError);
        return null;
      }
    }
  }

  async setItem(key, value) {
    try {
      if (!this.storage) await this.init();
      
      if (this.isCapacitor && this.storage.set) {
        // Use Capacitor Storage API
        await this.storage.set({ key, value });
      } else {
        // Use direct storage method
        await this.storage.set(key, value);
      }
      return true;
    } catch (error) {
      console.error('Storage set error:', error);
      // Fallback to localStorage
      try {
        localStorage.setItem(key, value);
        return true;
      } catch (fallbackError) {
        console.error('LocalStorage fallback error:', fallbackError);
        return false;
      }
    }
  }

  async removeItem(key) {
    try {
      if (!this.storage) await this.init();
      
      if (this.isCapacitor && this.storage.remove) {
        // Use Capacitor Storage API
        await this.storage.remove({ key });
      } else {
        // Use direct storage method
        await this.storage.remove(key);
      }
      return true;
    } catch (error) {
      console.error('Storage remove error:', error);
      // Fallback to localStorage
      try {
        localStorage.removeItem(key);
        return true;
      } catch (fallbackError) {
        console.error('LocalStorage fallback error:', fallbackError);
        return false;
      }
    }
  }

  async clear() {
    try {
      if (!this.storage) await this.init();
      
      if (this.isCapacitor && this.storage.clear) {
        // Use Capacitor Storage API
        await this.storage.clear();
      } else {
        // Use direct storage method
        await this.storage.clear();
      }
      return true;
    } catch (error) {
      console.error('Storage clear error:', error);
      // Fallback to localStorage
      try {
        localStorage.clear();
        return true;
      } catch (fallbackError) {
        console.error('LocalStorage fallback error:', fallbackError);
        return false;
      }
    }
  }

  // JSON methods for complex data
  async getJSON(key) {
    try {
      const value = await this.getItem(key);
      return value ? JSON.parse(value) : null;
    } catch (error) {
      console.error('Storage getJSON error:', error);
      return null;
    }
  }

  async setJSON(key, value) {
    try {
      const jsonString = JSON.stringify(value);
      return await this.setItem(key, jsonString);
    } catch (error) {
      console.error('Storage setJSON error:', error);
      return false;
    }
  }
}

// Create singleton instance
const unifiedStorage = new UnifiedStorage();

export default unifiedStorage;

// Session-specific methods
export const SessionStorage = {
  // Auth token management
  async setAuthToken(token, expiry) {
    await unifiedStorage.setItem('acctoo_auth_token', token);
    await unifiedStorage.setItem('acctoo_token_expiry', expiry.toString());
  },

  async getAuthToken() {
    const token = await unifiedStorage.getItem('acctoo_auth_token');
    const expiry = await unifiedStorage.getItem('acctoo_token_expiry');
    
    if (!token || !expiry) return null;
    
    if (Date.now() > parseInt(expiry)) {
      await this.clearAuthToken();
      return null;
    }
    
    return token;
  },

  async clearAuthToken() {
    await unifiedStorage.removeItem('acctoo_auth_token');
    await unifiedStorage.removeItem('acctoo_token_expiry');
  },

  // User data management
  async setUserData(userData) {
    return await unifiedStorage.setJSON('acctoo_user_data', userData);
  },

  async getUserData() {
    return await unifiedStorage.getJSON('acctoo_user_data');
  },

  async clearUserData() {
    await unifiedStorage.removeItem('acctoo_user_data');
  },

  // Company data management
  async setCompanyData(companyData) {
    return await unifiedStorage.setJSON('acctoo_companies', companyData);
  },

  async getCompanyData() {
    return await unifiedStorage.getJSON('acctoo_companies');
  },

  async clearCompanyData() {
    await unifiedStorage.removeItem('acctoo_companies');
  },

  // MPIN management
  async setMPIN(mpinData) {
    return await unifiedStorage.setJSON('acctoo_mpin_data', mpinData);
  },

  async getMPIN() {
    return await unifiedStorage.getJSON('acctoo_mpin_data');
  },

  async clearMPIN() {
    await unifiedStorage.removeItem('acctoo_mpin_data');
  },

  // Session state management
  async setSessionState(state) {
    return await unifiedStorage.setJSON('acctoo_session_state', state);
  },

  async getSessionState() {
    return await unifiedStorage.getJSON('acctoo_session_state');
  },

  async clearSessionState() {
    await unifiedStorage.removeItem('acctoo_session_state');
  },

  // Clear all session data
  async clearAll() {
    await this.clearAuthToken();
    await this.clearUserData();
    await this.clearCompanyData();
    await this.clearMPIN();
    await this.clearSessionState();
  }
};
